import { directorSkillSelectionFromState, type DirectorSkillSelection } from '../components/chat/directorSkillSelection'
import type { AutoConfirmCostSettings } from '../components/chat/autoConfirmCost'
import { createUploadAssetRef } from '../components/canvas-v2/nodes/shared/uploadAssetRef'
import type { YDocWriteRecord } from '../generated/nodeDataTypes'
import {
  labelForLandingUpload,
  landingUploadedReferencesFromUnknown,
  outputFieldForLandingUpload,
  type LandingUploadedReference,
} from '../components/landing/landingUploads'

type LocationStatePrimitive = string | number | boolean | null
type LocationStateValue = LocationStatePrimitive | readonly LocationStateValue[] | LocationStateObject
type LocationStateObject = { readonly [key: string]: LocationStateValue }

export type PendingAction = {
  tool: string
  args: Record<string, string>
}

export type WorkspaceLocationState = {
  initialSelectedSkill?: DirectorSkillSelection
  initialPrompt?: string
  initialUploadReferences: LandingUploadedReference[]
  initialReferencedNodeIds?: string[]
  initialAutoConfirmCostSettings?: AutoConfirmCostSettings
  /** Deterministic tool actions to execute before the Director's first turn.
   *  Used by the sequel flow: the source Director decided which library items
   *  to import; these are executed via execute-tool (no LLM) on workspace mount. */
  pendingActions?: PendingAction[]
  initialReplayScenario?: string
}

export type LandingUploadParseLog = {
  event: 'initial-upload-references-parsed'
  raw_type: string
  raw_count: number
  parsed_count: number
}

type LandingUploadLogger = (event: LandingUploadParseLog) => void

function isLocationStateObject(value: LocationStateValue | undefined): value is LocationStateObject {
  return value !== undefined && value !== null && typeof value === 'object' && !Array.isArray(value)
}

function dataValue(value: LocationStateValue | undefined, key: string): LocationStateValue | undefined {
  return isLocationStateObject(value) ? value[key] : undefined
}

function stringField(value: LocationStateValue | undefined, key: string): string | undefined {
  const entryValue = dataValue(value, key)
  return typeof entryValue === 'string' ? entryValue : undefined
}

function stringArrayField(value: LocationStateValue | undefined, key: string): string[] | undefined {
  const entryValue = dataValue(value, key)
  if (!Array.isArray(entryValue)) return undefined
  return entryValue.filter((item): item is string => typeof item === 'string')
}

function recordField(value: LocationStateValue | undefined, key: string): LocationStateObject | undefined {
  const field = dataValue(value, key)
  return isLocationStateObject(field) ? field : undefined
}

function locationStateValueKind(value: LocationStateValue | undefined): string {
  if (Array.isArray(value)) return 'array'
  if (value === null) return 'null'
  return typeof value
}

function autoConfirmCostSettingsField(
  value: LocationStateValue | undefined,
  key: string,
): AutoConfirmCostSettings | undefined {
  const field = recordField(value, key)
  if (!field) return undefined
  return typeof field.image === 'boolean' && typeof field.video === 'boolean'
    ? { image: field.image, video: field.video }
    : undefined
}

function parsePendingActions(value: LocationStateValue | undefined): PendingAction[] | undefined {
  const raw = dataValue(value, 'pendingActions')
  if (!Array.isArray(raw) || raw.length === 0) return undefined
  const actions: PendingAction[] = []
  for (const item of raw) {
    if (
      item !== null &&
      typeof item === 'object' &&
      !Array.isArray(item) &&
      typeof item.tool === 'string' &&
      item.args !== null &&
      typeof item.args === 'object' &&
      !Array.isArray(item.args)
    ) {
      const args: Record<string, string> = {}
      for (const [k, v] of Object.entries(item.args)) {
        if (typeof v === 'string') args[k] = v
      }
      actions.push({ tool: item.tool, args })
    }
  }
  return actions.length > 0 ? actions : undefined
}

export function landingUploadSeedTraceId(projectId: string, canvasId: string, nodeIds: string[]): string {
  return `landing-upload:seed:${projectId}:${canvasId}:${nodeIds.join(',')}`
}

export function workspaceLocationState(
  value: LocationStateValue | undefined,
  logLandingUpload?: LandingUploadLogger,
): WorkspaceLocationState {
  if (!value || typeof value !== 'object') {
    return { initialUploadReferences: [] }
  }

  const rawInitialUploadReferences = dataValue(value, 'initialUploadReferences')
  const initialUploadReferenceValue =
    typeof rawInitialUploadReferences === 'object' ? rawInitialUploadReferences : undefined
  const initialUploadReferences = landingUploadedReferencesFromUnknown(initialUploadReferenceValue)
  logLandingUpload?.({
    event: 'initial-upload-references-parsed',
    raw_type: locationStateValueKind(rawInitialUploadReferences),
    raw_count: Array.isArray(rawInitialUploadReferences) ? rawInitialUploadReferences.length : 0,
    parsed_count: initialUploadReferences.length,
  })

  return {
    initialPrompt: stringField(value, 'initialPrompt'),
    initialSelectedSkill: directorSkillSelectionFromState(value, 'initialSelectedSkill'),
    initialUploadReferences,
    initialReferencedNodeIds: stringArrayField(value, 'initialReferencedNodeIds'),
    initialAutoConfirmCostSettings: autoConfirmCostSettingsField(
      value,
      'initialAutoConfirmCostSettings',
    ),
    pendingActions: parsePendingActions(value),
    initialReplayScenario: stringField(value, 'initialReplayScenario'),
  }
}

export function createLandingUploadSeedNodeData(upload: LandingUploadedReference): YDocWriteRecord {
  const outputField = outputFieldForLandingUpload(upload.type)
  return {
    [outputField]: createUploadAssetRef(upload.path),
    in_file_name: upload.name,
    label: labelForLandingUpload(upload.name),
    status: 'completed',
  }
}
